import React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from '@fs/zion-router'
import { Button, DialogOverlayActions, H4, Paragraph, Separator } from '@fs/zion-ui'
import { ADD_EXISTING_PERSON_ENTRY_POINTS } from '../utils/addExistingPersonConstants'

/**
 * Confirms the relationships a move is about to break.
 *
 * Fires once per action rather than once per person. Whether it applies at all is the caller's
 * decision, via {@link ../utils/atRiskRelationships}.hasAtRiskRelationships.
 *
 * @param {Object} params
 * @param {Array<import('../utils/atRiskRelationships').AtRiskRelationshipGroup>} params.atRiskGroups
 *   The relationships that actually break, grouped by moving person
 * @param {string} params.entryPoint - One of {@link ADD_EXISTING_PERSON_ENTRY_POINTS}; picks the closing clause
 * @param {string} [params.focusPersonName] - Focus person's name, required for the relationships-card wording
 * @returns {React.ReactElement}
 */
const PreviousRelationshipsStep = ({ atRiskGroups = [], entryPoint, focusPersonName }) => {
  const [t] = useTranslation()

  const isSinglePerson = atRiskGroups.length === 1
  const openingCopy = isSinglePerson
    ? t(
        'add-existing-person.previous-relationships.body.one',
        '{name} is in another record with existing relationships.',
        { name: atRiskGroups[0]?.personName }
      )
    : t(
        'add-existing-person.previous-relationships.body.several',
        'The people below are in another record with existing relationships.'
      )

  const closingClause =
    entryPoint === ADD_EXISTING_PERSON_ENTRY_POINTS.RELATIONSHIPS_CARD
      ? t(
          'add-existing-person.previous-relationships.clause.existing-group',
          "To add them to {focusPersonName}'s group, the following relationships will be removed:",
          { focusPersonName }
        )
      : t(
          'add-existing-person.previous-relationships.clause.new-group',
          'To move them into a new group, the following relationships will be removed:'
        )

  return (
    <div data-testid="previous-relationships-step">
      <Paragraph size="sm">{`${openingCopy} ${closingClause}`}</Paragraph>
      <Separator />
      {atRiskGroups.map((group) => (
        <React.Fragment key={group.personId}>
          <H4 size="H6">{group.personName}</H4>
          <Separator size="xxs" />
          <ul data-testid={`at-risk-relationships-${group.personId}`}>
            {group.relationships.map((relationship) => (
              <li key={relationship.id}>
                <Paragraph size="sm">{`${relationship.counterpartName} - ${relationship.relationshipLabel}`}</Paragraph>
              </li>
            ))}
          </ul>
          <Separator />
        </React.Fragment>
      ))}
      <Paragraph size="xs" secondary>
        {t(
          'add-existing-person.previous-relationships.other-groups-unaffected',
          'Relationships in other groups are not affected.'
        )}
      </Paragraph>
    </div>
  )
}

/**
 * Footer for the Previous Relationships step.
 *
 * Rendered inside the dialog's memory router, so it can move the flow on itself. Uses `history.push`
 * rather than a `to` link: a link would scroll the page behind the dialog to the top and would route
 * the button's analytics through `trackLink` with a meaningless in-dialog URL.
 *
 * @param {Object} params
 * @param {string} [params.nextPath] - Route of the step that follows; omitted when this is the last one
 * @param {boolean} [params.saving=false] - Whether the save this step started is still in flight
 * @param {() => void} [params.onCancel] - Called when the patron backs out, returning them to the
 *   selection sheet with their picks intact rather than abandoning the flow
 * @param {() => void} [params.onContinue] - Called when the patron accepts the removals and no step follows
 * @returns {React.ReactElement}
 */
export const PreviousRelationshipsFooter = ({
  nextPath,
  saving = false,
  onCancel = () => {},
  onContinue = () => {},
}) => {
  const [t] = useTranslation()
  const history = useHistory()

  const handleContinue = () => (nextPath ? history.push(nextPath) : onContinue())

  return (
    <DialogOverlayActions>
      <Button
        color="gray"
        disabled={saving}
        onClick={onCancel}
        linkName="Previous Relationships Cancel"
        data-testid="previous-relationships-cancel-button"
      >
        {t('add-existing-person.previous-relationships.cancel.button', 'Cancel')}
      </Button>
      <Button
        emphasis="high"
        disabled={saving}
        onClick={handleContinue}
        linkName="Previous Relationships Remove"
        data-testid="previous-relationships-remove-button"
      >
        {saving
          ? t('navigation.saving', 'Saving...')
          : t('add-existing-person.previous-relationships.remove.button', 'Remove Relationships')}
      </Button>
    </DialogOverlayActions>
  )
}

export default PreviousRelationshipsStep
